/**
 * React Query hooks for user settings management
 */

import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type {
  ModuleName,
  SettingsSection,
  UserSettings,
  UserSettingsData,
} from "@/types/user-settings"

import {
  toggleFavoriteDocument as apiToggleFavoriteDocument,
  toggleFavoriteSession as apiToggleFavoriteSession,
  updateSettingsSection as apiUpdateSettingsSection,
  updateUserSettings as apiUpdateUserSettings,
  fetchUserSettings,
} from "@/api/user-settings-api"
import { queryKeys } from "@/lib/query-keys"

import { useInitSettings } from "./use-init-settings"

/**
 * The auth interceptor rejects a request with this message while the settings
 * singleton does not exist yet (`lib/api-client.ts`). It is a bootstrap-phase
 * condition, not a failure of the endpoint.
 */
const SETTINGS_NOT_INITIALIZED_MESSAGE = "settings are not initialized"

/** Bounded budget for the bootstrap condition: 30 x 250 ms ≈ 7.5 s. */
export const BOOTSTRAP_RETRY_LIMIT = 30
export const BOOTSTRAP_RETRY_DELAY_MS = 250
/** Budget for genuine failures — unchanged from the previous `retry: 1`. */
export const USER_SETTINGS_RETRY_LIMIT = 1
export const USER_SETTINGS_RETRY_DELAY_MS = 1000

/** True for the interceptor's "settings are not initialized yet" bootstrap error. */
export function isSettingsNotInitializedError(error: unknown): boolean {
  return (
    error instanceof Error && error.message.toLowerCase().includes(SETTINGS_NOT_INITIALIZED_MESSAGE)
  )
}

/**
 * Retry policy for the user-settings query.
 *
 * The app's first gate blocks on this query key, so a request that lost the race
 * with auth bootstrap must never eat the retry budget: TanStack would spend the
 * single retry on the bootstrap error and then pause it until a focus or
 * visibility transition, leaving the app on the boot spinner forever (#3083).
 * Bootstrap errors get their own bounded, fast budget; genuine failures keep the
 * original one-retry allowance.
 */
export function createUserSettingsRetry() {
  let bootstrapFailures = 0

  return function shouldRetry(failureCount: number, error: unknown): boolean {
    if (isSettingsNotInitializedError(error)) {
      bootstrapFailures += 1
      return bootstrapFailures <= BOOTSTRAP_RETRY_LIMIT
    }
    // `failureCount` counts every failure, so discount the bootstrap ones.
    return failureCount - bootstrapFailures < USER_SETTINGS_RETRY_LIMIT
  }
}

/** Retry fast while merely waiting for bootstrap; back off for real failures. */
export function userSettingsRetryDelay(_failureCount: number, error: unknown): number {
  return isSettingsNotInitializedError(error)
    ? BOOTSTRAP_RETRY_DELAY_MS
    : USER_SETTINGS_RETRY_DELAY_MS
}

/**
 * Query hook to fetch user settings with caching.
 *
 * The request carries the auth token held by the settings singleton, so the
 * query is gated on that singleton existing — here, in the hook, rather than in
 * each caller. Components that mount above the auth gate (e.g.
 * `HeavyPreviewLegacySeed`) therefore cannot fire a request that is guaranteed
 * to fail and poison the query key the app boot blocks on (#3083).
 */
export function useUserSettings(options?: { enabled?: boolean }) {
  const { isSettingsInitialized } = useInitSettings()
  // One counter per hook instance, kept across renders.
  const [retry] = useState(createUserSettingsRetry)

  return useQuery({
    queryKey: queryKeys.userSettings,
    queryFn: fetchUserSettings,
    // Requests carry the auth token from the settings singleton, so the query
    // stays disabled until that singleton exists, whatever the caller asked for.
    enabled: (options?.enabled ?? true) && isSettingsInitialized,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    // Refetch on focus is disabled deliberately. It causes cache churn that
    // races with optimistic updates (e.g. the GDPR compliance dialog briefly
    // reopening after acceptance). The 5-min staleTime + manual refresh on
    // mutations already keeps the data fresh.
    refetchOnWindowFocus: false,
    retry,
    retryDelay: userSettingsRetryDelay,
    // The app's first gate blocks on these settings, so it must not hinge on
    // `navigator.onLine`. A false negative there would pause the query before it
    // ever calls the API and leave the user staring at a screen that no refetch
    // can unstick. Attempting the request instead surfaces a real, retryable
    // error when the network genuinely is down.
    networkMode: "always",
  })
}

/**
 * Mutation hook for full settings update
 */
export function useUpdateUserSettings() {
  const queryClient = useQueryClient()

  return useMutation<UserSettings, Error, Partial<UserSettingsData>>({
    mutationFn: (data: Partial<UserSettingsData>) => apiUpdateUserSettings(data),
    onSuccess: (updatedSettings) => {
      // Update cache with new settings
      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, updatedSettings)
      toast.success("Settings updated successfully")
    },
    onError: () => {
      toast.error("Failed to update settings")
    },
  })
}

/**
 * Mutation hook for updating a specific settings section
 */
export function useUpdateSettingsSection() {
  const queryClient = useQueryClient()

  return useMutation<
    UserSettings,
    Error,
    { section: SettingsSection; data: Record<string, unknown> },
    { previousSettings: UserSettings | undefined }
  >({
    mutationFn: ({ section, data }) => apiUpdateSettingsSection(section, data),
    onMutate: async ({ section, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.userSettings })

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData<UserSettings>(queryKeys.userSettings)

      // Optimistically update the cache
      if (previousSettings) {
        // For dynamic sections (variants), need to preserve as dynamic properties
        const newData = {
          ...previousSettings.data,
          [section]: data,
        }

        queryClient.setQueryData<UserSettings>(queryKeys.userSettings, {
          ...previousSettings,
          data: newData as UserSettingsData,
        })
      }

      return { previousSettings }
    },
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, updatedSettings)
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData<UserSettings>(queryKeys.userSettings, context.previousSettings)
      }
      toast.error("Failed to update settings")
    },
  })
}

/**
 * Mutation hook for toggling favorite session status
 */
export function useToggleFavoriteSession() {
  const queryClient = useQueryClient()

  return useMutation<
    {
      session_id: string
      module: string
      is_favorite: boolean
      settings: UserSettings
    },
    Error,
    { module: ModuleName; sessionId: string },
    { previousSettings: UserSettings | undefined }
  >({
    mutationFn: ({ module, sessionId }) => apiToggleFavoriteSession(module, sessionId),
    onMutate: async ({ module, sessionId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.userSettings })

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData<UserSettings>(queryKeys.userSettings)

      // Optimistically update the cache
      if (previousSettings) {
        const favorites = previousSettings.data.favorite_sessions[module]
        const isFavorite = favorites.includes(sessionId)

        queryClient.setQueryData<UserSettings>(queryKeys.userSettings, {
          ...previousSettings,
          data: {
            ...previousSettings.data,
            favorite_sessions: {
              ...previousSettings.data.favorite_sessions,
              [module]: isFavorite
                ? favorites.filter((id) => id !== sessionId)
                : [...favorites, sessionId],
            },
          },
        })
      }

      return { previousSettings }
    },
    onSuccess: (response) => {
      // Update cache with server response
      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, response.settings)
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData<UserSettings>(queryKeys.userSettings, context.previousSettings)
      }
      toast.error("Failed to update favorite")
    },
  })
}

/**
 * Mutation hook for toggling favorite document status
 */
export function useToggleFavoriteDocument() {
  const queryClient = useQueryClient()

  return useMutation<
    {
      document_id: string
      is_favorite: boolean
      settings: UserSettings
    },
    Error,
    { documentId: string },
    { previousSettings: UserSettings | undefined }
  >({
    mutationFn: ({ documentId }) => apiToggleFavoriteDocument(documentId),
    onMutate: async ({ documentId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.userSettings })

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData<UserSettings>(queryKeys.userSettings)

      // Optimistically update the cache
      if (previousSettings) {
        const favorites = previousSettings.data.favorite_documents
        const isFavorite = favorites.includes(documentId)

        queryClient.setQueryData<UserSettings>(queryKeys.userSettings, {
          ...previousSettings,
          data: {
            ...previousSettings.data,
            favorite_documents: isFavorite
              ? favorites.filter((id) => id !== documentId)
              : [...favorites, documentId],
          },
        })
      }

      return { previousSettings }
    },
    onSuccess: (response) => {
      // Update cache with server response
      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, response.settings)
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData<UserSettings>(queryKeys.userSettings, context.previousSettings)
      }
      toast.error("Failed to update favorite")
    },
  })
}

/**
 * Helper hook to check if a session is favorited
 */
export function useIsFavoriteSession(module: ModuleName, sessionId: string | null) {
  const { data: settings } = useUserSettings() as { data: UserSettings | undefined }

  if (!sessionId || !settings) {
    return false
  }

  return settings.data.favorite_sessions[module].includes(sessionId)
}

/**
 * Helper hook to check if a document is favorited
 */
export function useIsFavoriteDocument(documentId: string | null) {
  const { data: settings } = useUserSettings() as { data: UserSettings | undefined }

  if (!documentId || !settings) {
    return false
  }

  return settings.data.favorite_documents.includes(documentId)
}
